// Async product typeahead for the S3 BOM component + substitute pickers (§4 S3).
//
// It searches the FULL product universe server-side via the products `q` filter
// (sku/name trigram) instead of choosing from a client-capped list. A fixed
// `<select>` fed by a `limit=200` page silently dropped every product past the
// cap — a real component beyond it was unpickable and rendered as unknown. This
// picker resolves any product regardless of catalogue size.
//
// The server is the only source. The selected value's label is resolved from
// `resolveLabel` (the caller's already-loaded product map) or remembered from a
// search selection, with a short-id fallback so a selection is never blank.
//
// Rev-7 (design-system §3.17 FilterBar, UP4): the suggestions open only while
// the box has focus and float over the page. The list used to render whenever
// a term stood in the box, so a term left behind after the reader moved on
// kept painting the list over the fields below it. It opens on focus or on
// typing, closes when focus leaves the picker, and Escape closes it and marks
// the key handled so a FilterBar panel around the picker stays open (one
// press, one layer). ArrowDown moves from the box into the list and the arrows
// walk it, ArrowUp from the first option returns to the box.

import { shortId as shortIdTail } from "../../../../lib/short-id";
import { useEffect, useId, useRef, useState } from "react";
import type { KeyboardEvent } from "react";
import { useTranslation } from "react-i18next";
import { useProducts } from "../hooks/catalogue-queries";

function shortId(id: string): string {
  return shortIdTail(id);
}

export function ProductPicker({
  id,
  value,
  onChange,
  placeholder,
  disabled = false,
  exclude,
  resolveLabel,
}: {
  id?: string | undefined;
  value: string;
  onChange: (productId: string) => void;
  placeholder: string;
  disabled?: boolean | undefined;
  exclude?: ReadonlySet<string> | undefined;
  /** Label for an already-selected id (from the caller's loaded product map). */
  resolveLabel?: ((productId: string) => string | undefined) | undefined;
}) {
  const { t } = useTranslation();
  const [term, setTerm] = useState("");
  const [open, setOpen] = useState(false);
  const listId = useId();
  const root = useRef<HTMLDivElement | null>(null);
  const input = useRef<HTMLInputElement | null>(null);
  // "Change" un-picks the product to pick another, so the box it reveals takes
  // focus straight away (the location picker's rule).
  const focusAfterClear = useRef(false);
  useEffect(() => {
    if (value.length === 0 && focusAfterClear.current) {
      focusAfterClear.current = false;
      input.current?.focus();
    }
  }, [value]);
  const q = term.trim();
  // Only search once the user types — an empty term shares one deduped fetch.
  const search = useProducts(q.length > 0 ? { q, limit: 20 } : {});
  const remembered = useRef<Map<string, string>>(new Map());

  const results = (search.data?.items ?? []).filter((p) => !exclude?.has(p.id));
  const showing = open && q.length > 0;

  const options = (): HTMLButtonElement[] =>
    Array.from(root.current?.querySelectorAll<HTMLButtonElement>(".product-picker-option") ?? []);

  const onKeyDown = (event: KeyboardEvent<HTMLDivElement>): void => {
    if (event.key === "Escape") {
      if (!showing) return;
      // Handled here: a FilterBar panel around the picker leaves this Escape
      // alone and closes on the next one.
      event.preventDefault();
      // Focus first: moving it from an option into the box fires the root's
      // onFocus, which opens the list, and the close must be the last word.
      input.current?.focus();
      setOpen(false);
      return;
    }
    if (event.key !== "ArrowDown" && event.key !== "ArrowUp") return;
    const list = options();
    if (list.length === 0) return;
    const at = list.findIndex((el) => el === document.activeElement);
    if (at === -1) {
      if (event.key !== "ArrowDown") return;
      event.preventDefault();
      setOpen(true);
      list[0]?.focus();
      return;
    }
    event.preventDefault();
    if (event.key === "ArrowUp" && at === 0) {
      input.current?.focus();
      return;
    }
    const next = event.key === "ArrowDown" ? Math.min(at + 1, list.length - 1) : at - 1;
    list[next]?.focus();
  };

  if (value.length > 0) {
    const label = remembered.current.get(value) ?? resolveLabel?.(value) ?? shortId(value);
    return (
      <div className="product-picker" data-testid="product-picker">
        <div className="product-picker-selected">
          <span className="product-picker-label" data-testid="product-picker-label">
            {label}
          </span>
          {disabled ? null : (
            <button
              type="button"
              className="ds-lnk"
              onClick={() => {
                focusAfterClear.current = true;
                onChange("");
              }}
            >
              {t("catalogue.bomEditor.picker.change")}
            </button>
          )}
        </div>
      </div>
    );
  }

  return (
    <div
      ref={root}
      className="product-picker"
      data-testid="product-picker"
      data-open={showing ? "true" : "false"}
      onFocus={() => {
        setOpen(true);
      }}
      onBlur={(event) => {
        const next = event.relatedTarget;
        if (next instanceof Node && root.current?.contains(next) === true) return;
        setOpen(false);
      }}
      onKeyDown={onKeyDown}
    >
      <input
        ref={input}
        id={id}
        type="search"
        className="bom-picker"
        role="combobox"
        aria-expanded={showing}
        aria-controls={showing ? listId : undefined}
        aria-autocomplete="list"
        autoComplete="off"
        value={term}
        disabled={disabled}
        placeholder={placeholder}
        onChange={(e) => {
          setTerm(e.target.value);
          setOpen(true);
        }}
      />
      {showing ? (
        <ul
          id={listId}
          className="product-picker-results"
          role="listbox"
          data-testid="product-picker-results"
        >
          {search.isPending ? (
            <li className="product-picker-hint" role="status">
              {t("catalogue.bomEditor.picker.searching")}
            </li>
          ) : search.error !== null ? (
            <li className="product-picker-hint form-error" role="alert">
              {t("catalogue.bomEditor.picker.failed")}
            </li>
          ) : results.length === 0 ? (
            <li className="product-picker-hint">{t("catalogue.bomEditor.picker.noMatches")}</li>
          ) : (
            results.map((p) => (
              <li key={p.id} role="option" aria-selected={false}>
                <button
                  type="button"
                  className="product-picker-option"
                  // Keep focus in the picker while the press lands, so the list
                  // does not close under the pointer before the click.
                  onMouseDown={(event) => {
                    event.preventDefault();
                  }}
                  onClick={() => {
                    remembered.current.set(p.id, `${p.sku} — ${p.name}`);
                    onChange(p.id);
                    setTerm("");
                    setOpen(false);
                  }}
                >
                  {p.sku} — {p.name}
                </button>
              </li>
            ))
          )}
        </ul>
      ) : null}
    </div>
  );
}
